import type { AreaData } from '@revelio/replots';

import type { DistributionPoint } from '../hooks/useSalaryDistribution';

/** Apply exchange rate to distribution x-values so the chart reflects converted amounts. */
export const convertDistribution = (
  distribution: DistributionPoint[],
  rate: number
): DistributionPoint[] => {
  if (rate === 1) return distribution;
  return distribution.map((d) => ({
    value: d.value * rate,
    kde: d.kde,
    cdf: d.cdf,
  }));
};

export const convertToAreaData = (
  distribution: DistributionPoint[],
  label: string
): AreaData[] => {
  if (distribution.length === 0) return [];
  return [
    {
      label,
      values: distribution
        .filter(
          (d) =>
            typeof d.value === 'number' &&
            typeof d.kde === 'number' &&
            // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
            !isNaN(d.value) &&
            // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
            !isNaN(d.kde)
        )
        .map((d) => ({
          x: d.value,
          value: d.kde,
          secondaryValue: d.cdf,
        })),
    },
  ];
};

/** Calculate the marker position as a percentage of the chart width.
 *  The AreaChart uses scaleLinear with domain [minX, maxX] and range [0, plotWidth].
 *  With showYAxis={false}, there's no y-axis offset, so position maps directly. */
export const calcMarkerPercent = (
  salary: number,
  areaData: AreaData[]
): number | null => {
  const values = areaData[0]?.values;
  if (!values || values.length === 0) return null;

  const xValues = values
    .map((v) => v.x)
    .filter((x): x is number => typeof x === 'number');
  if (xValues.length === 0) return null;
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  if (maxX <= minX) return null;

  const pct = ((salary - minX) / (maxX - minX)) * 100;
  return Math.max(0, Math.min(100, pct));
};

/** Return a color for a given percentile (0-100).
 *  Low percentiles = red (underpaid), high = green (well-paid). */
export const getPercentileColor = (percentile: number): string => {
  if (percentile >= 60) return '#38A169';
  if (percentile >= 40) return '#D69E2E';
  return '#E53E3E';
};
